import React, { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { Body2 } from "@activecollab/components";
import classNames from "classnames";
import PropTypes from "prop-types";
import { useUserCardContext } from "~/components/UserCard/context";
import AvatarContainer from "~/components/Users/AvatarContainer";
import { getUserById, isLoggedUserClientSelector } from "~/store/selectors/selector.users";
import Angie from "~/utils/angie.jsx";
import "./UserLink.css";

const UNKNOWN_USER = "Unknown user";

const UserLink = ({
  id,
  short,
  size,
  renderAs,
  nameProps,
  hideAvatar,
  hideName,
  name,
  email,
  className,
  notInteractive,
  context,
}) => {
  const user = useSelector((state) => getUserById(state, id));
  const isClient = useSelector(isLoggedUserClientSelector);
  const { setUserId } = useUserCardContext();

  const onClickHandler = useCallback(
    (e) => {
      if (context) {
        Angie.logger.info("UserLink clicked.", { context }, true);
      }

      if (user && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        e.stopPropagation();
        setUserId(id);
      }
    },
    [context, id, setUserId, user]
  );

  const resolvedName = useMemo(() => {
    if (user) {
      return !short ? user?.display_name : user?.short_display_name;
    }

    if (name) {
      return name;
    }

    Angie.logger.error(
      {
        message: "Unknown user in UserLink component.",
      },
      { context: context ? context : "not provided", user_link_id: id },
      true
    );

    return UNKNOWN_USER;
  }, [context, id, name, short, user]);

  const modifiedNameProps = useMemo(() => {
    return {
      overflow: "truncate",
      whitespace: "no-wrap",
      ...nameProps,
      className: classNames(
        "user-link-name",
        {
          "tw-no-underline hover:tw-text-secondary": !isClient && !notInteractive && resolvedName !== UNKNOWN_USER,
        },
        nameProps?.className
      ),
    };
  }, [isClient, nameProps, notInteractive, resolvedName]);

  const isInteractive = useMemo(() => {
    return !isClient && !notInteractive && resolvedName !== UNKNOWN_USER;
  }, [isClient, notInteractive, resolvedName]);

  const WrapperComponent = isInteractive ? "a" : "div";
  const NameComponent = renderAs ? renderAs : Body2;

  const resolvedHref = useMemo(() => {
    if (user) {
      return user?.is_trashed || user?.is_archived
        ? `mailto:${user?.get("email")}`
        : `${Angie.activecollab_url}${user?.url_path}`;
    }
    return `mailto:${email}`;
  }, [email, user]);

  const wrapperProps = useMemo(() => {
    const props = {
      className: classNames(
        "tw-inline-flex tw-gap-2 tw-items-center tw-overflow-hidden",
        { "tw-no-underline": isInteractive },
        className
      ),
    };

    if (isInteractive) {
      props.href = resolvedHref;
      props.onClick = onClickHandler;

      if (user && !user?.is_trashed && !user?.is_archived) {
        props.target = "_blank";
        props.rel = "noreferrer nofollow";
      }
    }

    return props;
  }, [isInteractive, onClickHandler, resolvedHref, user, className]);

  return (
    <WrapperComponent {...wrapperProps}>
      {!hideAvatar ? (
        <AvatarContainer
          id={id}
          email={email}
          name={name}
          size={size}
          className="user-link-avatar"
        />
      ) : null}
      {!hideName ? <NameComponent {...modifiedNameProps}>{resolvedName}</NameComponent> : null}
    </WrapperComponent>
  );
};

UserLink.propTypes = {
  context: PropTypes.string.isRequired,
  id: PropTypes.number,
  short: PropTypes.bool,
  size: PropTypes.number,
  renderAs: PropTypes.elementType,
  nameProps: PropTypes.object,
  hideAvatar: PropTypes.bool,
  name: PropTypes.string,
  email: PropTypes.string,
  className: PropTypes.string,
  hideName: PropTypes.bool,
  notInteractive: PropTypes.bool,
};

UserLink.defaultProps = {
  short: false,
  notInteractive: false,
};

UserLink.displayName = "UserLink";

export default UserLink;
